// Pure helpers for the Phone controller. Messaging itself stays on
// phoneSendSms (skyway-functions PHONE-A2). Nothing here talks to Twilio.

export const SMS_MAX = 1600;
export const PHONE_SOURCE = 'skyway-phone';

// Staff presets already include at least one of these. `phone` is the explicit
// grant; the rest let current accounts open the controller before an admin
// reapplies role presets. Vendor and driver presets include none of them.
export const PHONE_ACCESS_KEYS = [
  'phone',
  'leads',
  'lead',
  'search',
  'customer',
  'sales-inbox',
  'service-pipeline',
  'mechanic-orders',
  'inventory',
  'backoffice',
  'shipping-dashboard',
  'deal-dashboard',
];

export function canAccessPhone({ userType = '', whiteList = [], isAdmin = false } = {}) {
  if (isAdmin || userType === 'admin') return true;
  const list = Array.isArray(whiteList) ? whiteList : [];
  return PHONE_ACCESS_KEYS.some(key => list.includes(key));
}

export function normalizeUsPhone(raw) {
  if (raw == null) return { ok: false, e164: null, phone10: null };
  if (typeof raw !== 'string' && typeof raw !== 'number') {
    return { ok: false, e164: null, phone10: null };
  }
  let digits = String(raw).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) return { ok: false, e164: null, phone10: null };
  return { ok: true, e164: `+1${digits}`, phone10: digits };
}

export function formatUsPhone(raw) {
  const parsed = normalizeUsPhone(raw);
  if (!parsed.ok) {
    if (raw == null) return '';
    const text = String(raw).trim();
    return text;
  }
  const p = parsed.phone10;
  return `(${p.slice(0, 3)}) ${p.slice(3, 6)}-${p.slice(6)}`;
}

export function threadIdForPhone(raw) {
  const parsed = normalizeUsPhone(raw);
  if (!parsed.ok) return null;
  return `sms_${parsed.phone10}`;
}

// /phone/sms_9415550100 is the thread route. A missing doc still means "compose
// to this number" — the shop line has not texted them yet.
export function phoneFromThreadId(threadId) {
  const id = String(threadId || '');
  if (!id.startsWith('sms_')) return { ok: false, e164: null, phone10: null };
  return normalizeUsPhone(id.slice(4));
}

export function messageTime(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

export function isSkywayPhoneThread(thread) {
  if (!thread) return false;
  if (thread.source === PHONE_SOURCE || thread.channel === 'twilio-sms') return true;
  return String(thread.id || '').startsWith('sms_');
}

export function sortThreads(threads) {
  return [...(threads || [])].sort((a, b) => {
    const tb = messageTime(b && b.lastMessageDate)?.getTime() || 0;
    const ta = messageTime(a && a.lastMessageDate)?.getTime() || 0;
    return tb - ta;
  });
}

export function sortMessages(messages) {
  return [...(messages || [])].sort((a, b) => {
    const ta = messageTime(a && a.dateCreated)?.getTime() || 0;
    const tb = messageTime(b && b.dateCreated)?.getTime() || 0;
    return ta - tb;
  });
}

// Call history is the missed-call rows PHONE-A2 writes to phone_callbacks.
// PHONE-A1 answers voice calls and does not store them, so answered and
// outbound calls are absent here. This read does not use a softphone token.

const MISSED_CALL_STATUSES = new Set(['no-answer', 'busy', 'failed', 'canceled', 'cancelled']);

export function callHistoryThreadId({ thread, phone } = {}) {
  const fromPhone = threadIdForPhone(phone || thread?.phoneNumber || thread?.phone10);
  if (fromPhone) return fromPhone;
  const id = String(thread?.id || '');
  return id.startsWith('sms_') ? id : '';
}

export function normalizeCallEvent(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const id = String(raw.callSid || raw.id || '').trim();
  if (!id) return null;
  return {
    id,
    callSid: id,
    threadId: raw.threadId || '',
    fromE164: raw.fromE164 || '',
    direction: raw.direction || 'inbound',
    callStatus: raw.callStatus || '',
    dialCallStatus: raw.dialCallStatus || '',
    status: raw.status || '',
    createdAt: raw.createdAt || null,
    customerName: raw.customerName || '',
    note: raw.note || '',
    kind: raw.kind || '',
    source: raw.source || '',
    lastAt: raw.lastAt || null,
    count: Number(raw.count) || 1,
  };
}

export function callEventLabel(call) {
  const dial = String(call?.dialCallStatus || '').trim().toLowerCase();
  const status = String(call?.callStatus || '').trim().toLowerCase();
  const raw = dial || status;
  if (raw === 'busy') return 'Missed call · Busy';
  if (raw === 'failed') return 'Missed call · Failed';
  if (raw === 'canceled' || raw === 'cancelled') return 'Missed call · Canceled';
  if (!raw || raw === 'no-answer' || MISSED_CALL_STATUSES.has(raw)) return 'Missed call';
  if (raw === 'completed' || raw === 'answered') return 'Answered call';
  return 'Call';
}

export function callsForThread(calls, threadId) {
  if (!threadId) return [];
  return (calls || [])
    .map(call => normalizeCallEvent(call))
    .filter(call => call && (!call.threadId || call.threadId === threadId));
}

// SLED-088 callback queue. Open rows only, newest activity first (lastAt,
// else createdAt) so a repeat caller rises to the top. Sorted here, not in
// Firestore, so no composite index is needed. A row with no
// usable phone is kept so staff can still mark it done.
export function callbackQueueRows(raw) {
  return (raw || [])
    .map(row => normalizeCallEvent(row))
    .filter(row => row && (!row.status || row.status === 'open'))
    .sort((a, b) => {
      const at = messageTime(a.lastAt) || messageTime(a.createdAt);
      const bt = messageTime(b.lastAt) || messageTime(b.createdAt);
      return (bt ? bt.getTime() : 0) - (at ? at.getTime() : 0);
    });
}

export function callbackRowPhone(row) {
  if (row?.fromE164) return row.fromE164;
  const parsed = phoneFromThreadId(row?.threadId);
  return parsed.ok ? parsed.e164 : '';
}

export function callbackRowLabel(row) {
  if (row?.kind === 'callback-request') return 'Callback request';
  return callEventLabel(row);
}

export function callHistoryCaption(callsState, callCount) {
  if (callsState === 'error') return 'Call history didn’t load';
  if (callsState === 'ready' && !callCount) return 'No missed calls on file';
  return '';
}

// Texts and missed calls share one timeline, oldest first. A call with no
// timestamp stays at the start so it is not hidden under a later text.
export function mergeConversation(messages, calls) {
  const items = [];
  (messages || []).forEach(message => {
    const id = message.clientId || message.messageId || message.id;
    if (!id) return;
    items.push({
      type: 'message',
      id: `msg_${id}`,
      at: messageTime(message.dateCreated)?.getTime() || 0,
      message,
    });
  });
  (calls || []).forEach(call => {
    const normalized = call && call.callSid ? call : normalizeCallEvent(call);
    if (!normalized) return;
    items.push({
      type: 'call',
      id: `call_${normalized.id}`,
      at: messageTime(normalized.createdAt)?.getTime() || 0,
      call: normalized,
    });
  });
  items.sort((a, b) => {
    if (a.at !== b.at) return a.at - b.at;
    if (a.type !== b.type) return a.type === 'call' ? -1 : 1;
    return String(a.id).localeCompare(String(b.id));
  });
  return items;
}

function displayName(thread) {
  if (!thread) return '';
  return String(thread.customerName || thread.contact?.name || '').trim();
}

function firstLetter(word) {
  const chars = Array.from(word || '');
  for (const ch of chars) {
    if (ch.toLowerCase() !== ch.toUpperCase()) return ch.toUpperCase();
  }
  return '';
}

export function threadTitle(thread) {
  if (!thread) return 'Unknown number';
  const name = displayName(thread);
  if (name) return name;
  return formatUsPhone(thread.phoneNumber || thread.phone10) || 'Unknown number';
}

// Avatar text. Do not slice threadTitle: with no customer name that title is a
// formatted number and the first character is "(".
export function threadInitials(thread) {
  const name = displayName(thread);
  const letters = name.split(/\s+/).map(firstLetter).filter(Boolean);
  if (letters.length === 1) return letters[0];
  if (letters.length > 1) return `${letters[0]}${letters[letters.length - 1]}`;

  const raw = (thread && (thread.phoneNumber || thread.phone10)) || name;
  const parsed = normalizeUsPhone(raw);
  const digits = parsed.ok ? parsed.phone10 : String(raw || '').replace(/\D/g, '');
  if (digits.length >= 4) return digits.slice(-4);
  return '';
}

export function threadPreview(thread) {
  const body = String(thread?.lastMessageBody || '').replace(/\s+/g, ' ').trim();
  if (!body) return 'No messages yet';
  return body.length > 90 ? `${body.slice(0, 87)}…` : body;
}

function trimmed(value) {
  if (value == null) return '';
  return String(value).trim();
}

// Algolia tokenizes phone_number on punctuation, so "9415550100" will not hit
// "(941) 555-0100". Three digit groups match, and the caller still requires an
// exact 10-digit equality before a hit is shown.
export function customerSearchQuery(raw) {
  const parsed = normalizeUsPhone(raw);
  if (!parsed.ok) return '';
  const p = parsed.phone10;
  return `${p.slice(0, 3)} ${p.slice(3, 6)} ${p.slice(6)}`;
}

export function phonesMatch(a, b) {
  const left = normalizeUsPhone(a);
  const right = normalizeUsPhone(b);
  return left.ok && right.ok && left.phone10 === right.phone10;
}

export function customerDisplayName(customer) {
  if (!customer || typeof customer !== 'object') return '';
  const name = trimmed(customer.name);
  if (name) return name;
  return `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
}

export function customersMatchingPhone(customers, phone) {
  if (!normalizeUsPhone(phone).ok) return [];
  const out = [];
  const seen = new Set();
  for (const customer of customers || []) {
    if (!customer || typeof customer !== 'object') continue;
    if (!phonesMatch(customer.phone || customer.phone_number, phone)) continue;
    const id = trimmed(customer.id || customer.customerId);
    const name = customerDisplayName(customer);
    if (!id && !name) continue;
    const key = id || name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      id,
      name,
      email: trimmed(customer.email),
      phone: trimmed(customer.phone || customer.phone_number),
    });
  }
  return out;
}

function leadName(lead) {
  return trimmed(lead && lead.name);
}

function addCar(cars, seen, car) {
  const label = trimmed(car && car.label);
  const stock = trimmed(car && car.stock);
  const key = (stock || label).toLowerCase();
  if (!key || seen.has(key)) return;
  seen.add(key);
  cars.push({
    stock,
    label: label || stock,
    leadId: trimmed(car && car.leadId),
  });
}

function describedCar(lead) {
  const ymm = [lead.year, lead.make, lead.model].map(trimmed).filter(Boolean).join(' ');
  return ymm || trimmed(lead.car);
}

// Cars the lead lookup actually returned. Free-text interest stays a label
// with no stock, so the pane cannot open a car that was never an id.
export function carsFromLeadPayload(leads) {
  const cars = [];
  const seen = new Set();
  for (const lead of leads || []) {
    if (!lead || typeof lead !== 'object') continue;
    const stock = trimmed(lead.stock);
    const described = describedCar(lead);
    if (stock) {
      addCar(cars, seen, {
        stock,
        label: described && described !== stock ? `${stock} · ${described}` : stock,
        leadId: lead.id,
      });
    } else if (described) {
      addCar(cars, seen, { stock: '', label: described, leadId: lead.id });
    }
    for (const field of ['carsInterested', 'carsOwned']) {
      const items = Array.isArray(lead[field]) ? lead[field] : [];
      for (const item of items) {
        if (typeof item === 'string') {
          addCar(cars, seen, { stock: '', label: item, leadId: lead.id });
        } else if (item && typeof item === 'object') {
          const itemStock = trimmed(item.stock);
          const label = trimmed(item.label || item.name || item.car) || itemStock;
          addCar(cars, seen, {
            stock: itemStock,
            label: itemStock && label && label !== itemStock ? `${itemStock} · ${label}` : label,
            leadId: lead.id,
          });
        }
      }
    }
  }
  return cars;
}

function ordersFrom(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (Array.isArray(payload.serviceOrders)) return payload.serviceOrders;
  if (Array.isArray(payload.orders)) return payload.orders;
  return null;
}

// lookupLeadsByPhone and searchCustomers do not return service orders. An
// absent list stays unavailable so the pane can say so instead of inventing
// an empty shop history.
export function serviceOrdersFromPayload(leadLookup, customerSearch) {
  const fromLead = ordersFrom(leadLookup);
  const fromCustomer = ordersFrom(customerSearch);
  if (!fromLead && !fromCustomer) return { available: false, orders: [] };
  const orders = [];
  const seen = new Set();
  for (const row of [...(fromLead || []), ...(fromCustomer || [])]) {
    if (!row || typeof row !== 'object') continue;
    const id = trimmed(row.id || row.stock || row.orderId);
    const label = trimmed(row.label || row.stock || row.id || row.orderId);
    if (!id && !label) continue;
    const key = id || label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    orders.push({
      id,
      label: label || id,
      status: trimmed(row.status),
      car: trimmed(row.car || row.car_title),
    });
  }
  return { available: true, orders };
}

export function loadingPhoneContext(phone10) {
  return {
    status: 'loading',
    phone10: phone10 || '',
    leadsOk: false,
    customersOk: false,
    leadError: null,
    customerError: null,
    leads: [],
    primaryId: null,
    customers: [],
    cars: [],
    serviceOrders: [],
    serviceOrdersAvailable: false,
    displayName: '',
  };
}

// Fold lookupLeadsByPhone + searchCustomers into what the list, header, and
// context pane render. Failed calls stay empty. Nothing here fabricates a
// lead, customer, car, or service order.
export function buildPhoneContext({ phone, leadLookup, customerSearch } = {}) {
  const parsed = normalizeUsPhone(phone);
  const leadsOk = !!(leadLookup && leadLookup.success !== false && !leadLookup.error);
  const leads = leadsOk && Array.isArray(leadLookup.leads)
    ? leadLookup.leads.filter(lead => lead && trimmed(lead.id))
    : [];
  const requestedPrimary = leadsOk ? trimmed(leadLookup.primaryId) : '';
  const primaryId = leads.some(lead => lead.id === requestedPrimary)
    ? requestedPrimary
    : (leads[0] ? leads[0].id : null);

  const customersOk = !!(customerSearch && customerSearch.success !== false && !customerSearch.error);
  const rawCustomers = customersOk && Array.isArray(customerSearch.customers)
    ? customerSearch.customers
    : [];
  const customers = customersMatchingPhone(rawCustomers, phone);
  const cars = carsFromLeadPayload(leads);
  const serviceOrders = serviceOrdersFromPayload(
    leadsOk ? leadLookup : null,
    customersOk ? customerSearch : null,
  );
  const primary = leads.find(lead => lead.id === primaryId) || null;
  const displayName = leadName(primary) || customers.map(customer => customer.name).find(Boolean) || '';

  return {
    status: leadsOk || customersOk ? 'ready' : 'error',
    phone10: parsed.ok ? parsed.phone10 : '',
    leadsOk,
    customersOk,
    leadError: leadsOk ? null : trimmed(leadLookup && leadLookup.error) || 'Lead lookup failed',
    customerError: customersOk ? null : trimmed(customerSearch && customerSearch.error) || 'Customer lookup failed',
    leads,
    primaryId,
    customers,
    cars,
    serviceOrders: serviceOrders.orders,
    serviceOrdersAvailable: serviceOrders.available,
    displayName,
  };
}

// A resolved lead or customer name replaces the formatted number. A lookup
// with no name leaves the thread's stored customer name alone. CRM leads do
// not pass a preferred lead into Phone.
export function threadForDisplay(thread, context) {
  if (!thread) return thread;
  const name = context && context.status === 'ready' ? trimmed(context.displayName) : '';
  if (!name || trimmed(thread.customerName) === name) return thread;
  return { ...thread, customerName: name };
}

// The phone lookup is the source of truth. A ?fromLead= hint is ignored so
// opening Phone does not jump back to the lead a salesperson just left.
export function openLeadId(thread, context) {
  if (context && context.status === 'ready' && context.primaryId) return context.primaryId;
  return trimmed(thread && thread.leadId);
}

export function filterThreads(threads, searchTerm) {
  const term = String(searchTerm || '').trim().toLowerCase();
  const sorted = sortThreads((threads || []).filter(isSkywayPhoneThread));
  if (!term) return sorted;
  const digits = term.replace(/\D/g, '');
  return sorted.filter(thread => {
    const title = threadTitle(thread).toLowerCase();
    const preview = String(thread.lastMessageBody || '').toLowerCase();
    const phone = String(thread.phoneNumber || thread.phone10 || '');
    if (title.includes(term) || preview.includes(term)) return true;
    if (digits && phone.replace(/\D/g, '').includes(digits)) return true;
    return false;
  });
}

// Staff send. The cloud function owns the From number (pilot DID) and writes
// the thread. Callers must not add Twilio credentials or a from-number.
export function outboundSmsPayload({ to, body, leadId } = {}) {
  const phone = normalizeUsPhone(to);
  if (!phone.ok) return { ok: false, error: 'Enter a US phone number' };
  const text = typeof body === 'string' ? body.trim() : '';
  if (!text) return { ok: false, error: 'Enter a message' };
  if (text.length > SMS_MAX) {
    return { ok: false, error: 'Message is too long (1600 character limit)' };
  }
  const variables = { to: phone.e164, body: text };
  const lead = leadId == null ? '' : String(leadId).trim();
  if (lead) variables.leadId = lead;
  return { ok: true, variables, threadId: `sms_${phone.phone10}` };
}

// A pending bubble can match a message the shop line already stored. Allow a
// little clock skew, and ignore anything written long after the tap.
const OPTIMISTIC_MATCH_SKEW_MS = 15 * 1000;
const OPTIMISTIC_MATCH_WINDOW_MS = 2 * 60 * 1000;

// Local outbound row shown before phoneSendSms returns. The cloud function
// still owns the real message id. This object never goes to Twilio.
export function createOptimisticOutbound({
  to,
  body,
  threadId,
  leadId = '',
  sentBy = '',
  knownMessageIds = [],
  clientId,
  now = new Date(),
} = {}) {
  const created = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const phone = normalizeUsPhone(to);
  const text = typeof body === 'string' ? body.trim() : '';
  return {
    clientId: clientId || `local_${created.getTime()}_${Math.random().toString(36).slice(2, 10)}`,
    optimistic: true,
    threadId: threadId || (phone.ok ? `sms_${phone.phone10}` : ''),
    to: phone.ok ? phone.e164 : '',
    body: text,
    leadId: leadId == null ? '' : String(leadId),
    direction: 'outbound',
    status: 'pending',
    dateCreated: created,
    sentBy: sentBy || '',
    serverMessageId: '',
    error: '',
    knownMessageIds: (knownMessageIds || []).filter(Boolean),
  };
}

// Success clears the pending flag. Failure stays failed so the bubble can be
// retried. A missing response is a failure, never a silent success.
export function settleOptimistic(message, response) {
  if (!message) return message;
  if (!response || response.success === false) {
    return {
      ...message,
      status: 'failed',
      error: (response && response.error) || 'Failed to send text',
      serverMessageId: '',
    };
  }
  return {
    ...message,
    status: 'delivered',
    error: '',
    serverMessageId: response.messageId || message.serverMessageId || '',
  };
}

export function outboundStatusLabel(status) {
  if (status === 'pending') return 'Sending';
  if (status === 'delivered') return 'Delivered';
  if (status === 'failed' || status === 'undelivered') return '';
  return status ? String(status) : '';
}

export function serverMessageMatchesOptimistic(message, optimistic) {
  if (!message || !optimistic) return false;
  const serverId = message.messageId || message.id || '';
  if (optimistic.serverMessageId && serverId && optimistic.serverMessageId === serverId) {
    return true;
  }
  if (optimistic.status === 'failed') return false;
  if (message.direction !== 'outbound') return false;
  if (String(message.body || '').trim() !== String(optimistic.body || '').trim()) return false;
  if (serverId && (optimistic.knownMessageIds || []).includes(serverId)) return false;
  const serverTime = messageTime(message.dateCreated)?.getTime();
  const optTime = messageTime(optimistic.dateCreated)?.getTime();
  if (serverTime == null || optTime == null) return false;
  return serverTime >= optTime - OPTIMISTIC_MATCH_SKEW_MS
    && serverTime <= optTime + OPTIMISTIC_MATCH_WINDOW_MS;
}

// Server rows stay. A pending row drops once the stored outbound is in the
// list, so the thread does not show the same text twice. A failed row stays
// until a retry settles it.
export function mergeThreadMessages(serverMessages, optimisticList) {
  const server = serverMessages || [];
  const claimed = new Set();
  const visible = [];
  (optimisticList || []).forEach(opt => {
    const idx = server.findIndex((msg, i) => !claimed.has(i) && serverMessageMatchesOptimistic(msg, opt));
    if (idx === -1) visible.push(opt);
    else claimed.add(idx);
  });
  return sortMessages([...server, ...visible]);
}

export function presenceLabel(status) {
  if (status === 'available') return 'Available';
  if (status === 'away') return 'Away';
  return 'No status';
}

export function salesDisplayName(user) {
  if (!user) return 'Sales';
  const fromParts = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  if (fromParts) return fromParts;
  if (user.name) return String(user.name).trim() || 'Sales';
  return 'Sales';
}

export function rosterPerson(user) {
  const presence = user?.phonePresence === 'available' || user?.phonePresence === 'away'
    ? user.phonePresence
    : 'unset';
  return {
    id: user.id,
    name: salesDisplayName(user),
    phone: user.phone || '',
    presence,
    archived: user.archived === true,
  };
}
